import { fmtDate, sameDisplayDay } from './format';

/**
 * The line under a candidate's name on a Feedback card: WHO assessed them, WHEN the interview was
 * held, and WHEN the write-up landed.
 *
 * Theresa, Oct 2026. Two things the card could not say before:
 *
 *  - **Whose verdict it is.** A recruiter reading "No hire" wants the name without opening
 *    anything; on a case or final round there are usually two.
 *  - **Which date is which.** The card showed ONE date — the interview's, falling back to the
 *    write-up's and labelled "Interview date" either way, so a fallback quietly misnamed itself.
 *    They can be days apart, and the gap is the thing: a verdict written up a week late is a week
 *    of the candidate waiting, which is what decides whether the next mail goes out now.
 *
 * Its own file because CandidateCard.tsx is eight lines under the 500-line ceiling.
 */

/** Three names in a card byline is a wall; past two it counts. */
function nameLine(names: string[]): string | null {
  const clean = names.filter(Boolean);
  if (clean.length === 0) return null;
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} & ${clean[1]}`;
  return `${clean[0]} +${clean.length - 1}`;
}

export function FeedbackByline({
  interviewers,
  interviewAt,
  completedAt,
}: {
  interviewers?: string[];
  /** When the interview was held. Absent when no held panel is recorded for the stage. */
  interviewAt?: string | null;
  /** When the write-up was submitted. Always present on a card that has feedback. */
  completedAt: string;
}) {
  const who = nameLine(interviewers || []);
  return (
    <>
      {who && <span title="Who gave this feedback">· {who}</span>}
      {interviewAt && <span title="When the interview was held">· Interview {fmtDate(interviewAt)}</span>}
      {/* Never two dates when there is only one day's worth of news. */}
      <span title="When the feedback was submitted">
        ·{' '}
        {interviewAt && sameDisplayDay(interviewAt, completedAt)
          ? 'Feedback same day'
          : `Feedback ${fmtDate(completedAt)}`}
      </span>
    </>
  );
}
